// Droits BeOne : `allRights` de /me est une table plate { 'da.xxx': '0' | '1' | '2' }, globale à l'utilisateur
// (identique dans tous les workspaces). Valeurs inversées, comme dans le BeOne historique (lib/user/RightUser.php) :
//   '0' = droit complet, '1' = droit intermédiaire (consultation / élément grisé), '2' ou clé absente = pas de droit.
// Aucune hiérarchie entre clés : 'da.customer' n'implique pas 'da.customer.search'.

export type RightsMap = Readonly<Record<string, string>>;

export type RightLevel = 'full' | 'limited' | 'none';

export const rightLevel = (rights: RightsMap, key: string): RightLevel => {
  const value = rights[key];
  if (value === '0') return 'full';
  if (value === '1') return 'limited';
  return 'none';
};

// Une règle d'accès : fonction pure des droits, composable avec anyOf / allOf
export type Rule = (rights: RightsMap) => boolean;

// Élément visible (droit complet ou intermédiaire) : menus, pages, liens
export const visible = (key: string): Rule => rights => rightLevel(rights, key) !== 'none';

// Fonctionnalité utilisable : bouton actif, option proposée
export const full = (key: string): Rule => rights => rightLevel(rights, key) === 'full';

// Drapeau de restriction : certains droits BeOne retirent une capacité quand ils valent '0'
// (ex. da.customer.lock_export, da.badges.hide). La règle est vraie tant que la restriction ne s'applique pas.
export const notRestrictedBy = (key: string): Rule => rights => rightLevel(rights, key) !== 'full';

// Variante pour les restrictions vérifiées en « < 2 » dans BeOne (ex. da.query.no_extract) : même '1' restreint
export const notRestrictedAtAllBy = (key: string): Rule => rights => rightLevel(rights, key) === 'none';

export const anyOf = (...rules: Rule[]): Rule => rights => rules.some(rule => rule(rights));

export const allOf = (...rules: Rule[]): Rule => rights => rules.every(rule => rule(rights));

export const always: Rule = () => true;

// État d'un contrôle d'interface dérivé d'un niveau : complet = actif, intermédiaire = grisé, aucun = masqué
export type ControlState = 'enabled' | 'disabled' | 'hidden';

export const controlState = (level: RightLevel): ControlState =>
  level === 'full' ? 'enabled' : level === 'limited' ? 'disabled' : 'hidden';

// Le plus restrictif de plusieurs niveaux (ex. droit du canal ET droit de création sur ce canal)
export const weakestLevel = (...levels: RightLevel[]): RightLevel =>
  levels.includes('none') ? 'none' : levels.includes('limited') ? 'limited' : 'full';
